/**
 * Recent Projects Component
 * Grid of recent project cards
 * Aviation Dashboard Style
 */

import { Link, useNavigate } from 'react-router-dom';
import { Image, AlertTriangle, Plus, ArrowRight } from 'lucide-react';
import { type DBProject } from '../../db';
import { ProjectMapThumbnail } from '../projects/ProjectMapThumbnail';

interface RecentProjectsProps {
  projects: (DBProject & { boundingBox?: { north: number; south: number; east: number; west: number } })[];
}

// Status badge for aviation style
function AviationStatusBadge({ status }: { status: string }) {
  const getStatusStyle = (status: string) => {
    switch (status) {
      case 'COMPLETED':
      case 'EXPORTED':
        return 'bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-400 border-green-200 dark:border-green-500/30';
      case 'IN_PROGRESS':
        return 'bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-500/30';
      case 'PENDING':
        return 'bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-500/30';
      default:
        return 'bg-slate-100 dark:bg-slate-500/20 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-500/30';
    }
  };

  return (
    <span className={`px-2 py-0.5 text-[10px] font-mono font-medium uppercase tracking-wider rounded border ${getStatusStyle(status)}`}>
      {status.replace('_', ' ')}
    </span>
  );
}

export function RecentProjects({ projects }: RecentProjectsProps) {
  const navigate = useNavigate();

  if (projects.length === 0) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <button
          onClick={() => navigate('/projects')}
          className="project-card border-2 border-dashed border-slate-300 dark:border-slate-600 p-6 flex flex-col items-center justify-center gap-3 hover:border-blue-400 dark:hover:border-blue-500/50 transition-colors min-h-[200px]"
        >
          <div className="w-12 h-12 bg-blue-100 dark:bg-blue-500/20 rounded-full flex items-center justify-center">
            <Plus className="w-6 h-6 text-blue-600 dark:text-blue-400" />
          </div>
          <span className="font-mono font-medium text-slate-500 dark:text-slate-400 text-sm">Create your first project</span>
        </button>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {projects.map((project) => (
        <Link
          key={project.id}
          to={`/projects/${project.id}`}
          className="project-card p-4 group"
        >
          {/* Map Thumbnail */}
          <ProjectMapThumbnail
            boundingBox={project.boundingBox}
            className="aspect-video mb-3 relative"
          />

          {/* Project info */}
          <div>
            <h3 className="font-mono font-semibold text-slate-800 dark:text-slate-100 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors text-sm tracking-wide">
              {project.code}
            </h3>
            <p className="text-xs font-mono text-slate-500 truncate mt-0.5">{project.name}</p>
          </div>

          {/* Stats */}
          <div className="flex items-center gap-4 mt-3 text-xs font-mono text-slate-500 dark:text-slate-400">
            <div className="flex items-center gap-1">
              <Image className="w-3.5 h-3.5" />
              <span>{project.photoCount}</span>
            </div>
            <div className="flex items-center gap-1">
              <AlertTriangle className="w-3.5 h-3.5" />
              <span>{project.anomalyCount}</span>
            </div>
          </div>

          {/* Progress and status */}
          <div className="mt-3 space-y-2">
            <div className="flex items-center justify-between">
              <AviationStatusBadge status={project.status} />
              <span className="text-xs font-mono text-slate-500 tabular-nums">{project.progress}%</span>
            </div>
            <div className="progress-bar-aviation h-1">
              <div 
                className="progress-fill" 
                style={{ width: `${project.progress}%` }}
              />
            </div>
          </div>

          {/* Hover indicator */}
          <div className="mt-3 flex items-center justify-end opacity-0 group-hover:opacity-100 transition-opacity">
            <span className="text-xs font-mono text-blue-600 dark:text-blue-400 flex items-center gap-1">
              Open <ArrowRight className="w-3 h-3" />
            </span>
          </div>
        </Link>
      ))}

      {/* Add new project card */}
      <button
        onClick={() => navigate('/projects')}
        className="project-card border-2 border-dashed border-slate-300 dark:border-slate-600 p-6 flex flex-col items-center justify-center gap-3 hover:border-blue-400 dark:hover:border-blue-500/50 transition-colors min-h-[200px]"
      >
        <div className="w-12 h-12 bg-blue-100 dark:bg-blue-500/20 rounded-full flex items-center justify-center border border-blue-200 dark:border-blue-500/30">
          <Plus className="w-6 h-6 text-blue-600 dark:text-blue-400" />
        </div>
        <span className="font-mono font-medium text-slate-500 dark:text-slate-400 text-sm">New Project</span>
      </button>
    </div>
  );
}
